import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, Sparkles, MousePointerClick, Volume2, Play } from 'lucide-react';
import { soundService, SoundPrefs, SoundName, SOUNDS_ENABLED } from '../services/soundService';
import { useAuth } from '../contexts/AuthContext';
import { profileService } from '../services/profileService';

const Switch: React.FC<{ id: string; checked: boolean; disabled?: boolean; onChange: (v: boolean) => void }> = ({ id, checked, disabled, onChange }) => (
  <button
    id={id}
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative shrink-0 w-11 h-6 rounded-full transition-colors duration-300 cursor-pointer disabled:opacity-40 ${checked ? 'bg-primary' : 'bg-tint-stone'}`}
  >
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-surface shadow-sm transition-transform duration-300 ${checked ? 'translate-x-5' : ''}`} />
  </button>
);

const Preview: React.FC<{ name: SoundName; label: string }> = ({ name, label }) => (
  <button
    type="button"
    onClick={() => soundService.play(name, true)}
    aria-label={label}
    className="shrink-0 w-8 h-8 rounded-full text-muted hover:text-primary hover:bg-tint-stone flex items-center justify-center transition-colors duration-300 cursor-pointer"
  >
    <Play size={13} strokeWidth={1.75} />
  </button>
);

const Row: React.FC<{ icon: React.ReactNode; title: string; desc: string; children: React.ReactNode }> = ({ icon, title, desc, children }) => (
  <div className="flex items-center gap-4 p-4 rounded-2xl border border-black/[0.06] dark:border-white/[0.06]">
    <span className="shrink-0 w-9 h-9 rounded-full bg-tint-peach text-ink flex items-center justify-center">{icon}</span>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-ink">{title}</p>
      <p className="text-[12px] leading-snug text-muted">{desc}</p>
    </div>
    <div className="flex items-center gap-1">{children}</div>
  </div>
);

export const NotificationSoundSettings: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<SoundPrefs>(soundService.getPrefs());
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(typeof Notification !== 'undefined' && Notification.permission === 'denied');

  useEffect(() => soundService.subscribe(setPrefs), []);

  const togglePush = async (on: boolean) => {
    setBusy(true);
    soundService.setPrefs({ push: on });
    try {
      const tokenKey = user?.id ? `libritui_last_fcm_${user.id}` : '';
      if (on) {
        const { requestFirebaseNotificationPermission } = await import('../services/firebase');
        const token = await requestFirebaseNotificationPermission();
        setBlocked(typeof Notification !== 'undefined' && Notification.permission === 'denied');
        if (token && user?.id) {
          await profileService.updateProfile(user.id, { fcm_token: token } as any);
          localStorage.setItem(tokenKey, token);
        }
      } else if (user?.id) {
        // Sem token salvo, o servidor deixa de enviar notificações para este aparelho
        await profileService.updateProfile(user.id, { fcm_token: null } as any);
        localStorage.removeItem(tokenKey);
      }
    } catch (e) {
      console.warn('[Notificação & Som] Falha ao alterar notificações:', e);
    }
    setBusy(false);
  };

  return (
    <div className="space-y-2">
      <Row
        icon={<Bell size={16} strokeWidth={1.75} />}
        title={t('soundSettings.push', 'Notificações no dispositivo')}
        desc={blocked ? t('soundSettings.pushBlocked', 'Bloqueadas no navegador. Libere nas configurações do site.') : t('soundSettings.pushDesc', 'Lembretes de estudo e avisos de amigos.')}
      >
        <Switch id="pref-push" checked={prefs.push && !blocked} disabled={busy || blocked} onChange={togglePush} />
      </Row>
      {SOUNDS_ENABLED && (<>
      <Row
        icon={<Sparkles size={16} strokeWidth={1.75} />}
        title={t('soundSettings.effects', 'Sons de efeito')}
        desc={t('soundSettings.effectsDesc', 'Acertos, erros, notas salvas e missão do dia.')}
      >
        <Preview name="success" label={t('soundSettings.preview', 'Ouvir')} />
        <Switch id="pref-effects" checked={prefs.effects} onChange={(v) => soundService.setPrefs({ effects: v })} />
      </Row>
      <Row
        icon={<MousePointerClick size={16} strokeWidth={1.75} />}
        title={t('soundSettings.buttons', 'Som dos botões')}
        desc={t('soundSettings.buttonsDesc', 'Um toque discreto ao tocar em botões.')}
      >
        <Preview name="tap" label={t('soundSettings.preview', 'Ouvir')} />
        <Switch id="pref-buttons" checked={prefs.buttons} onChange={(v) => soundService.setPrefs({ buttons: v })} />
      </Row>
      <div className="p-4 rounded-2xl border border-black/[0.06] dark:border-white/[0.06]">
        <label htmlFor="pref-volume" className="flex items-center gap-4">
          <span className="shrink-0 w-9 h-9 rounded-full bg-tint-peach text-ink flex items-center justify-center"><Volume2 size={16} strokeWidth={1.75} /></span>
          <span className="flex-1 text-sm font-medium text-ink">{t('soundSettings.volume', 'Volume')}</span>
          <span className="text-[12px] text-muted tabular-nums">{Math.round(prefs.volume * 100)}%</span>
        </label>
        <input
          id="pref-volume"
          type="range"
          min={0}
          max={100}
          step={5}
          value={Math.round(prefs.volume * 100)}
          onChange={(e) => soundService.setPrefs({ volume: Number(e.target.value) / 100 })}
          onPointerUp={() => soundService.play('notify', true)}
          className="mt-3 w-full accent-[rgb(var(--primary-rgb))] cursor-pointer"
        />
      </div>
      <button
        type="button"
        onClick={() => soundService.play('logo', true)}
        className="w-full h-11 rounded-full text-[13px] font-medium text-muted hover:text-ink flex items-center justify-center gap-2 transition-colors duration-300 cursor-pointer"
      >
        <Play size={13} strokeWidth={1.75} />
        {t('soundSettings.signature', 'Ouvir a assinatura LibriTui')}
      </button>
      </>)}
    </div>
  );
};
